import { extendTheme } from "@mui/joy/styles";
import type { CssVarsThemeOptions } from "@mui/joy/styles";
import { placeholderStyles } from "../shared/inputStyles";

// Custom color palette - bluish-gray with orange highlights
export const defaultColors = {
  primary: {
    50: "#fffbf0",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b", // Main orange - brighter and more yellow
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
  },
  neutral: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b", // Main bluish-gray
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
  success: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
  },
  warning: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
  },
  danger: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
  },
};

// Non-colour options shared between the base theme and any runtime-merged org theme.
// Must be kept in sync with any org-theme merge so typography and component tokens
// are not lost when a nested CssVarsProvider is applied.
export const sharedThemeOptions: CssVarsThemeOptions = {
  fontFamily: {
    display: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    body: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    xl2: "1.5rem",
    xl3: "1.875rem",
    xl4: "2.25rem",
  },
  typography: {
    h1: {
      color: "var(--joy-palette-primary-500)",
      fontWeight: 400,
    },
    h2: {
      color: "var(--joy-palette-primary-500)",
      fontWeight: 400,
    },
    h3: {
      color: "var(--joy-palette-primary-500)",
      fontWeight: 400,
    },
    h4: {
      color: "var(--joy-palette-primary-500)",
      fontWeight: 400,
    },
    "title-lg": {
      fontWeight: 400,
    },
    "title-md": {
      fontWeight: 400,
    },
    "title-sm": {
      fontWeight: 400,
    },
  },
  components: {
    JoyButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          borderRadius: "8px",
          minHeight: "40px",
          padding: "8px 16px",
          minWidth: "100px",
          wordWrap: "break-word",
          whiteSpace: "normal",
        },
      },
    },
    JoyInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
        input: {
          ...placeholderStyles,
        },
      },
    },
    JoyTextarea: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
        textarea: {
          ...placeholderStyles,
        },
      },
    },
    JoySelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyCard: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--joy-palette-background-surface)",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          borderRadius: "12px",
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyTable: {
      styleOverrides: {
        root: {
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyModal: {
      styleOverrides: {
        backdrop: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        root: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      },
    },
    JoyChip: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          fontWeight: 500,
        },
      },
    },
    JoyBadge: {
      styleOverrides: {
        badge: {
          borderRadius: "10px",
          fontWeight: 600,
        },
      },
    },
    JoyTooltip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: "var(--joy-palette-background-level1)",
          color: "var(--joy-palette-text-primary)",
          border: "1px solid var(--joy-palette-divider)",
        },
      },
    },
    JoyMenu: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    JoyCircularProgress: {
      defaultProps: {
        thickness: 3,
      },
    },
    JoyAccordion: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          overflow: "hidden",
        },
      },
    },
    JoyAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: "8px",
        },
      },
    },
    JoyAccordionSummary: {
      styleOverrides: {
        button: {
          transition: "all 0.2s ease",
        },
      },
    },
    JoySlider: {
      styleOverrides: {
        root: {
          "--Slider-thumbSize": "12px",
          "--Slider-thumbWidth": "12px",
        },
      },
    },
  },
};

// Create the Joy UI theme with default colors
export const defaultTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: defaultColors.primary,
        neutral: defaultColors.neutral,
        success: defaultColors.success,
        warning: defaultColors.warning,
        danger: defaultColors.danger,
        background: {
          body: "#f5f5f5",
          surface: "#f8fafc",
          level1: "#ffffff",
          level2: "#f1f5f9",
          level3: "#e2e8f0",
        },
        text: {
          primary: "#0f172a",
          secondary: "#475569",
          tertiary: "#64748b",
        },
        divider: "#e2e8f0",
        focusVisible: "var(--joy-palette-primary-500)",
      },
    },
    dark: {
      palette: {
        primary: defaultColors.primary,
        neutral: defaultColors.neutral,
        success: defaultColors.success,
        warning: defaultColors.warning,
        danger: defaultColors.danger,
        background: {
          body: "#000000",
          surface: "#181820",
          level1: "#334155",
          level2: "#475569",
          level3: "#64748b",
        },
        text: {
          primary: "#f8fafc",
          secondary: "#cbd5e1",
          tertiary: "#94a3b8",
        },
        divider: "#303040",
        focusVisible: "var(--joy-palette-primary-500)",
      },
    },
  },
  ...sharedThemeOptions,
});
